import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import SourceBadge from '../../components/SourceBadge';
import { useTerritories } from '../../context/TerritoriesContext';
import { useConfig } from '../../context/ConfigContext';
import { subscribeLeads } from '../../data/listeners/leads';
import { filterAndSortLeads } from '../../data/leadQuery';
import { isNoTouch14d } from '../../data/cadence';
import {
  STAGES, STAGE_COLOR, STAGE_BG, normalizeStage, leadValueOf,
} from '../../data/leadConstants';

const peso = (n) => `₱${Math.round(n).toLocaleString()}`;

function StageBadge({ stage }) {
  const s = normalizeStage(stage);
  return (
    <span className="badge" style={{ background: STAGE_BG[s], color: STAGE_COLOR[s] }}>
      <span className="dot" style={{ background: STAGE_COLOR[s] }} />
      {s}
    </span>
  );
}

const QUICK = ['All', 'Overdue', 'No touch 14+d', 'Hot', 'Anchors'];
// v1.1 source-tracing chips — appended only when the loaded rows contain a
// sourced (chatbot) lead, so tenants without a chatbot see no new chips (DoR-33).
const SOURCE_QUICK = ['Inbound', 'Manual'];

function passesQuick(l, quick) {
  if (quick === 'Overdue') return l.nextDate && l.nextDate < Date.now() && !['Won', 'Lost'].includes(l.stage);
  if (quick === 'No touch 14+d') return isNoTouch14d(l.lastTouchAt);
  if (quick === 'Hot') return (l.leadScore ?? 0) >= 8;
  if (quick === 'Anchors') return l.tier === 'Anchor';
  if (quick === 'Inbound') return Boolean(l.source);
  if (quick === 'Manual') return !l.source;
  return true;
}

// Leads table — filters, quick chips, search, stat-row. Rows open the shared drawer.
// `ownerFilter` (manager salesman filter) is applied by the parent's selection.
export default function LeadList({ ownerFilter = '' }) {
  const { tenantId, role, territoryIds } = useAuth();
  const { openLead } = useUI();
  const isMobile = useIsMobile();
  const { territories, terrLabel } = useTerritories();
  const { config } = useConfig();
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState('');
  const [quick, setQuick] = useState('All');
  // Archived leads keep their outcomes in every KPI (Jorwin 2026-07-21 ruling —
  // archive only de-clutters views); this toggle lets the TABLE show them inline.
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({
    stage: '', territory: '', category: '', tier: '', sortDir: 'desc',
  });

  useEffect(() => {
    setError('');
    return subscribeLeads(
      { tenantId, role, territoryIds },
      setLeads,
      (e) => setError(e.message || 'Could not load leads.'),
    );
  }, [tenantId, role, territoryIds]);

  // Archived rows are hidden by default (CRM revisions Task 2) but toggleable
  // back in — dimmed, so they read as "closed history", not active pipeline.
  const visible = useMemo(() => leads.filter((l) => showArchived || !l.archived), [leads, showArchived]);

  const rows = useMemo(() => {
    const base = filterAndSortLeads(visible, filters)
      .filter((l) => !ownerFilter || l.ownerUid === ownerFilter)
      .filter((l) => passesQuick(l, quick))
      .filter((l) => !q || (l.businessName ?? '').toLowerCase().includes(q.toLowerCase()));
    return base;
  }, [visible, filters, quick, q, ownerFilter]);

  const stats = useMemo(() => ({
    total: rows.length,
    won: visible.filter((l) => l.stage === 'Won').length,
    active: visible.filter((l) => ['Contacted', 'Negotiating'].includes(normalizeStage(l.stage))).length,
    anchor: visible.filter((l) => l.tier === 'Anchor').length,
  }), [rows.length, visible]);

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div>
      <div className="stat-row">
        <div className="stat"><div className="n">{stats.total}</div><div className="l">Total in view</div></div>
        <div className="stat"><div className="n">{stats.won}</div><div className="l">Won</div></div>
        <div className="stat"><div className="n">{stats.active}</div><div className="l">Active pipeline</div></div>
        <div className="stat"><div className="n">{stats.anchor}</div><div className="l">Anchor accounts</div></div>
      </div>

      <div className="chips">
        {(visible.some((l) => l.source) ? [...QUICK, ...SOURCE_QUICK] : QUICK).map((qf) => (
          <span key={qf} className={`chip ${quick === qf ? 'on' : ''}`} onClick={() => setQuick(qf)} style={{ cursor: 'pointer' }}>{qf}</span>
        ))}
        <span
          className={`chip ${showArchived ? 'on' : ''}`}
          onClick={() => setShowArchived((v) => !v)}
          style={{ cursor: 'pointer', marginLeft: 'auto' }}
          title="Archived leads stay in all KPIs; this only shows/hides their rows here."
        >
          {showArchived ? 'Archived: shown' : 'Show archived'}
        </span>
      </div>

      <div className="toolbar">
        <div className="searchbox"><input placeholder="Search business…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select className="chip-select" value={filters.stage} onChange={set('stage')}>
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="chip-select" value={filters.territory} onChange={set('territory')}>
          <option value="">All territories</option>
          {territories.map((t) => <option key={t.id} value={t.id}>{t.label ?? t.id}</option>)}
        </select>
        <select className="chip-select" value={filters.category} onChange={set('category')}>
          <option value="">All channels</option>
          {(config.channels ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="chip-select" value={filters.tier} onChange={set('tier')}>
          <option value="">All tiers</option>
          {(config.tiers ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="seg" role="group" aria-label="Sort by score">
          <button type="button" className={filters.sortDir === 'desc' ? 'on' : ''} onClick={() => setFilters((f) => ({ ...f, sortDir: 'desc' }))}>Score ↓</button>
          <button type="button" className={filters.sortDir === 'asc' ? 'on' : ''} onClick={() => setFilters((f) => ({ ...f, sortDir: 'asc' }))}>Score ↑</button>
        </div>
        <span style={{ marginLeft: 'auto', color: 'var(--color-fg-muted)', fontSize: 'var(--text-small)' }}>{rows.length} of {leads.length}</span>
      </div>

      {error && <div className="form-error">{error}</div>}

      {isMobile ? (
        <div className="lead-cards">
          {rows.map((l) => (
            <div key={l.id} className="lead-card" onClick={() => openLead(l.id)}>
              <div className="lc-top"><span className="lc-name">{l.businessName}<SourceBadge lead={l} /></span><StageBadge stage={l.stage} /></div>
              <div className="lc-meta">
                <span>{peso(leadValueOf(l))}/mo</span>
                <span>{terrLabel(l.territoryId)}</span>
                {l.tier && <span>{l.tier}</span>}
                {l.category && <span>{l.category}</span>}
                {l.nextDate && <span>Next: {new Date(l.nextDate).toLocaleDateString()}</span>}
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div style={{ color: 'var(--color-fg-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No leads match these filters.</div>
          )}
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Business</th><th>Channel</th><th>Territory</th><th>Tier</th><th>Stage</th><th>Score</th><th>Value ₱/mo</th></tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} className="click" style={l.archived ? { opacity: 0.55 } : undefined} onClick={() => openLead(l.id)}>
                  <td style={{ fontWeight: 'var(--font-semibold)' }}>{l.businessName}<SourceBadge lead={l} /></td>
                  <td>{l.category}</td>
                  <td>{terrLabel(l.territoryId)}</td>
                  <td><span className={`badge tier-${l.tier}`}>{l.tier}</span></td>
                  <td><StageBadge stage={l.stage} /></td>
                  <td>{l.leadScore}</td>
                  <td>{peso(leadValueOf(l))}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} style={{ color: 'var(--color-fg-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No leads match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
